import { ReactNode, MouseEvent, useEffect, useMemo, useState, useCallback, useRef } from 'react';
// mui imports
import { Menu, Stack, MenuItem, Typography, Skeleton } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// third party imports
import { useIntl } from 'react-intl';
import { customScrollBar, MerchantChipType, MerchantAvatar, ShowMoreButton } from './components';
// types
import { Box, Breakpoint, StackProps } from '@mui/system';
import { MerchantType } from 'types/components/shared';
import { useDispatch, useSelector } from 'store';
import PerfectScrollbar from 'react-perfect-scrollbar';
import useCurrentMediaQuery from 'hooks/useCurrentMediaQuery';
import { updateGlobalMerchant } from 'store/slices/auth';
import { MakeRequired } from 'types';

export type MultiMerchantProps = {
    blockDefaults?: boolean;
    defaultSelected: MerchantType[];
    filterMerchants?: { merchantId: number }[] | null;
    justOne?: boolean;
    maxShow?: number;
    onChange?: (selectedMerchats: MerchantType[]) => void;
    readOnly?: boolean;
    size?: 'small' | 'medium' | 'large';
    show?: boolean;
};
/**
 *
 * @deprecated
 */
export default function MultiMerchant({
    show = true,
    blockDefaults = true,
    defaultSelected,
    filterMerchants,
    justOne = false,
    maxShow = 3,
    onChange,
    readOnly,
    size = 'large'
}: MultiMerchantProps) {
    // hooks
    const intl = useIntl();

    // store
    const { merchants } = useSelector((state) => state.auth);

    const [merchantsList, setMerchantsList] = useState<MerchantChipType[]>([]);
    const [firstRender, setFirstRender] = useState<boolean>(true);
    const isAllSelected = useMemo(() => merchantsList.every((item) => item.isSelected), [merchantsList]);
    // menu triggers
    const [anchorMenu, setAnchorMenu] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorMenu);

    // change event
    useEffect(() => {
        const selectedMerchants = merchantsList.filter((item) => item.isSelected);
        if (onChange && selectedMerchants.length) {
            onChange(selectedMerchants);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merchantsList]);

    // create initial array
    useEffect(() => {
        if (firstRender && merchants && defaultSelected) {
            const filteredMerchants =
                filterMerchants && filterMerchants.length > 0
                    ? merchants.filter((item) => filterMerchants.some((itemA) => itemA.merchantId === item.merchantId))
                    : merchants;
            const defaultSelectedIds = defaultSelected.map((ds) => ds.merchantId);

            const initialList = filteredMerchants?.map((item) => ({
                ...item,
                isSelected: defaultSelectedIds.length ? defaultSelectedIds.includes(item.merchantId) : item.isFather
            }));
            setMerchantsList(initialList);
            setFirstRender(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [merchants, defaultSelected, filterMerchants]);

    // menu actions - open
    const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
        setAnchorMenu(event.currentTarget);
    };
    // menu actions - close
    const handleCloseMenu = () => {
        setAnchorMenu(null);
    };
    // toggle buttons (external)
    const toggleItem = (merchant: MerchantChipType) => {
        if (readOnly) return;
        // only select one
        if (justOne) {
            const newList = merchantsList.map((item) => {
                if (item.merchantId === merchant.merchantId) {
                    return { ...item, isSelected: true };
                }
                return { ...item, isSelected: false };
            });
            setMerchantsList([...newList]);
            return;
        }
        const isDefaultSelected = defaultSelected.some((itemA) => itemA.merchantId === merchant.merchantId);
        const isBlocked = (isDefaultSelected && blockDefaults) || merchant.isFather;
        if (!isBlocked) {
            const newList = merchantsList.map((item) => {
                if (item.merchantId === merchant.merchantId) {
                    return { ...item, isSelected: !item.isSelected };
                }
                return item;
            });
            setMerchantsList([...newList]);
        }
    };
    // toggle between select all and unselect
    const selectAll = useCallback(() => {
        if (readOnly) return;
        if (!isAllSelected) {
            handleCloseMenu();
        }
        const newVal = !isAllSelected;
        if (newVal) {
            // set all as selected
            const newList = merchantsList.map((item) => ({ ...item, isSelected: true }));
            setMerchantsList([...newList]);
            // handleChangeList(newList);
        } else {
            const newList = merchantsList.map((merchant) => {
                const isDefaultSelected = defaultSelected.some((itemA) => itemA.merchantId === merchant.merchantId);
                const isBlocked = isDefaultSelected && blockDefaults;
                if (merchant.isFather || isBlocked) {
                    return merchant;
                }
                return { ...merchant, isSelected: false };
            });
            setMerchantsList([...newList]);
            // handleChangeList(newList);
        }
    }, [blockDefaults, defaultSelected, isAllSelected, merchantsList, readOnly]);

    const filteredElementsToRender = useMemo<MerchantChipType[]>(() => {
        if (justOne && merchantsList.length < maxShow) {
            return [...merchantsList];
        }
        if (justOne) {
            return [...merchantsList.filter((item) => item.isSelected)];
        }

        let elements: MerchantChipType[] = [];
        // add father as first element
        const fatherElement = merchantsList.find((item) => item.isFather);
        if (fatherElement) {
            elements = [...elements, fatherElement];
        }
        merchantsList.forEach((item) => {
            if (!item.isFather && elements.length <= maxShow) {
                elements = [...elements, item];
            }
        });
        return elements.slice(0, maxShow);
    }, [justOne, merchantsList, maxShow]);

    const moreText = useMemo(() => {
        if (justOne && merchantsList.length < maxShow) {
            return 0;
        }
        if (justOne) {
            return merchantsList.length - 1;
        }
        return merchantsList.length - maxShow;
    }, [justOne, maxShow, merchantsList.length]);

    const renderMore = useMemo<boolean>(
        () => (merchantsList.length > maxShow || justOne!) && moreText > 0,
        [justOne, maxShow, merchantsList.length, moreText]
    );
    const renderMenu = useMemo<ReactNode>(() => {
        if (!renderMore) return null;
        return (
            <Menu
                anchorEl={anchorMenu}
                id="account-menu"
                open={open}
                onClose={handleCloseMenu}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflowY: 'scroll',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        maxHeight: 450,
                        minWidth: 200,
                        paddingVertical: 1,
                        mt: 1.5,
                        ...customScrollBar,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            fontSize: 14,
                            ml: -0.5,
                            mr: 1
                        },
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 14,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0
                        }
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {!justOne && !readOnly && (
                    <MenuItem onClick={selectAll} selected={isAllSelected}>
                        <CheckCircleIcon color="success" />
                        <Typography variant="body1" sx={{ ml: 1, fontSize: 17 }}>
                            {intl.formatMessage({
                                id: 'select_all'
                            })}
                        </Typography>
                    </MenuItem>
                )}
                {merchantsList.map((merchant) => (
                    <MenuItem
                        key={`merchant-menu-item-${merchant.merchantId}`}
                        onClick={() => {
                            toggleItem(merchant);
                        }}
                        selected={merchant.isSelected}
                        sx={{ cursor: readOnly ? 'initial' : 'pointer' }}
                    >
                        <MerchantAvatar readOnly feedback={false} merchant={merchant} size="medium" />
                        <Typography variant="body1" sx={{ ml: 1, fontSize: 17 }}>
                            {merchant.name}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchorMenu, isAllSelected, merchantsList, open, renderMore]);

    if (!show) return null;
    return (
        <Stack direction="row" justifyContent="flex-end" alignItems="center">
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    listStyle: 'none',
                    p: 0.5,
                    m: 0,
                    background: 'transparent'
                }}
                component="ul"
            >
                <>
                    {filteredElementsToRender.map((merchant) => (
                        <MerchantAvatar
                            containerStyles={{ mr: 1 }}
                            readOnly={readOnly}
                            key={`merchant-avatar-item-${merchant.merchantId}`}
                            merchant={merchant}
                            handleClick={() => toggleItem(merchant)}
                            size={size}
                        />
                    ))}
                    {renderMore}
                </>
            </Box>
            {renderMore && (
                <ShowMoreButton
                    size={size}
                    handleClick={handleOpenMenu}
                    moreText={`${(merchants?.length ?? 0) - filteredElementsToRender.length}`}
                />
            )}

            {renderMenu}
        </Stack>
    );
}
const multiDefaultProps = {
    onChange: () => {},
    readOnly: false,
    maxShow: 3,
    justOne: false,
    size: 'large'
};

MultiMerchant.defaultProps = multiDefaultProps;

export type MerchantSelectorType<Multiple> = Multiple extends true ? Array<MerchantType> : MerchantType;

type MerchantSelectorProps = {
    multiple?: boolean;
    onChange?: (value: Array<MerchantType>) => void;
    blockFather?: boolean;
    size?: 'small' | 'medium' | 'large';
    value: Array<MerchantType> | null;
    minimal?: boolean;
    overlap?: boolean;
    maxRows?: number;
    merchantsToShow?: number[] | MerchantType[];
    onAvatarClick?: (merchant: MerchantType, event: MouseEvent<HTMLDivElement | HTMLLIElement, globalThis.MouseEvent>) => void;
    disabled?: boolean;
    readOnly?: boolean;
    showName?: boolean;
    direction?: StackProps['direction'];
    spacing?: StackProps['spacing'];
};

/**
 * MerchantsSelector Component: For list with multiple selections. Use for forms, list with multiple selections
 *
 *
 * Props:
 * - onChange: function to handle updates on selection
 * - size: determines the size of the selector (default is 'medium')
 * - minimal: if true, shows fewer merchants by default and moves the rest to a menu
 * - value: array of selected merchants
 * - multiple: allows multiple selections if true (default is true)
 * - blockFather: prevents unselecting "isFather" merchants if true (default is true)
 */
export const MerchantsSelector = ({
    onChange,
    size = 'medium',
    minimal = false,
    value: selectedMerchants,
    multiple = true,
    blockFather = true,
    overlap = false,
    maxRows,
    merchantsToShow,
    onAvatarClick,
    disabled = false,
    readOnly = false,
    showName = false,
    direction = 'row',
    spacing
}: MerchantSelectorProps) => {
    // hooks
    const currentBreakPoint = useCurrentMediaQuery();

    // store
    const { drawerOpen } = useSelector((state) => state.menu);
    const { merchants: globalMerchants, loading } = useSelector((state) => state.auth);

    const [anchorMenu, setAnchorMenu] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorMenu);

    // limit
    const [avatarsLimit, setAvatarsLimit] = useState<number>(3);

    useEffect(() => {
        if (!maxRows) {
            setAvatarsLimit(getAvatarCounter(currentBreakPoint, drawerOpen, minimal) ?? 0);
        } else {
            setAvatarsLimit(maxRows);
        }
    }, [currentBreakPoint, drawerOpen, maxRows, minimal]);

    const previousWithinLimitRef = useRef<MakeRequired<MerchantType, 'isSelected'>[]>([]);
    const previousBeyondLimitRef = useRef<MakeRequired<MerchantType, 'isSelected'>[]>([]);

    const merchants: MakeRequired<MerchantType, 'isSelected'>[] = useMemo(() => {
        if (!globalMerchants) return [];

        let filteredMerchants: MakeRequired<MerchantType, 'isSelected'>[] = globalMerchants.map((item) => ({
            ...item,
            isSelected: false
        })) as MakeRequired<MerchantType, 'isSelected'>[];

        if (merchantsToShow) {
            const merchantsIds =
                typeof merchantsToShow[0] === 'number'
                    ? (merchantsToShow as number[])
                    : (merchantsToShow as MerchantType[]).map((item) => item.merchantId);

            filteredMerchants = filteredMerchants.filter((itemMerchant) => merchantsIds.includes(itemMerchant.merchantId));
        }

        const selectedMerchantIds = new Set((selectedMerchants ?? []).map((item) => item.merchantId));

        filteredMerchants = filteredMerchants.map((item) => ({
            ...item,
            isSelected: selectedMerchantIds.has(item.merchantId)
        }));

        // Use `reduce` to separate withinLimit and beyondLimit based on avatarsLimit
        const { withinLimit, beyondLimit } = filteredMerchants.reduce(
            (acc, merchant, index) => {
                if (index < avatarsLimit) {
                    acc.withinLimit.push(merchant);
                } else {
                    acc.beyondLimit.push(merchant);
                }
                return acc;
            },
            {
                withinLimit: [] as MakeRequired<MerchantType, 'isSelected'>[],
                beyondLimit: [] as MakeRequired<MerchantType, 'isSelected'>[]
            }
        );

        const selectedInBeyondLimit = beyondLimit.filter((merchant) => merchant.isSelected);
        const selectedInWithinLimit = withinLimit.filter((merchant) => merchant.isSelected);

        const nonSelectedInBeyondLimit = beyondLimit.filter((merchant) => !merchant.isSelected);

        // Logic to swap selected items if needed
        if (selectedMerchantIds.size === 1 && selectedInBeyondLimit.length > 0) {
            const selectedIndexItem = selectedInBeyondLimit.findIndex((item) => item.isSelected);
            const lastViewIndex = withinLimit.length - 1;

            if (selectedIndexItem !== -1 && lastViewIndex >= 0) {
                const selectedItem = selectedInBeyondLimit[selectedIndexItem];
                const lastViewItem = withinLimit[lastViewIndex];

                selectedInBeyondLimit[selectedIndexItem] = lastViewItem;
                withinLimit[lastViewIndex] = selectedItem;

                beyondLimit.length = 0;
                beyondLimit.push(...nonSelectedInBeyondLimit, ...selectedInBeyondLimit);
                withinLimit[lastViewIndex] = selectedItem;
            }
        }

        // Logic for multiple selected merchants
        if (selectedMerchantIds.size > 1 && selectedInWithinLimit.length === 0) {
            const firstSelectedIndex = selectedInBeyondLimit.findIndex((item) => item.isSelected);

            if (firstSelectedIndex !== -1) {
                const firstSelectedItem = selectedInBeyondLimit[firstSelectedIndex];
                const firstWithinLimitItem = withinLimit.shift();

                withinLimit.push(firstSelectedItem);

                selectedInBeyondLimit.splice(firstSelectedIndex, 1);

                beyondLimit.length = 0;

                if (firstWithinLimitItem) {
                    beyondLimit.unshift(firstWithinLimitItem);
                }

                beyondLimit.push(...nonSelectedInBeyondLimit, ...selectedInBeyondLimit);
            }
        }

        const finalList = [...withinLimit]
            .sort((a, b) => (a.merchantId < b.merchantId ? -1 : 1))
            .concat([...beyondLimit].sort((a, b) => (a.merchantId < b.merchantId ? -1 : 1)));

        // Update refs to track previous lists
        previousWithinLimitRef.current = withinLimit;
        previousBeyondLimitRef.current = beyondLimit;

        return finalList;
    }, [avatarsLimit, globalMerchants, merchantsToShow, selectedMerchants]);

    const handleAvatarClick = (
        merchant: MerchantType,
        isSelected: boolean,
        event: MouseEvent<HTMLDivElement | HTMLLIElement, globalThis.MouseEvent>
    ) => {
        if (disabled || readOnly) {
            return;
        }

        let prev = selectedMerchants;

        if (onAvatarClick) {
            onAvatarClick(merchant, event);
        }

        // Prevent the list from being empty
        if (prev?.length === 1 && isSelected) {
            return;
        }

        if (prev === null) return;

        // If "isFather" is protected, prevent unselection
        if (blockFather && merchant.isFather && isSelected && onChange) {
            onChange(prev);
            return;
        }

        // If multiple selection is not allowed, select only the current merchant
        if (!multiple && prev && onChange) {
            onChange([merchant]);

            return;
        }

        // If the merchant is selected, deselect it from the list
        if (prev && isSelected && onChange) {
            onChange(prev.filter((item) => item.merchantId !== merchant.merchantId));
            return;
        }

        // If the merchant is not selected, add it to the list
        if (onChange && !isSelected && onChange) {
            onChange([...(prev ?? []), merchant]);
        }
    };

    const avatarsMenu = useMemo(() => {
        return merchants?.slice(avatarsLimit);
    }, [avatarsLimit, merchants]);

    const optionsMenu = useMemo(() => {
        return (
            <>
                <Menu
                    anchorEl={anchorMenu}
                    id="account-menu"
                    open={open}
                    sx={{
                        width: 350
                        // bgcolor: 'background.paper', zIndex: 10 ,
                    }}
                    // placement="bottom-end"
                    onClose={() => setAnchorMenu(null)}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                    <PerfectScrollbar>
                        <Stack sx={{ maxHeight: 400 }}>
                            {avatarsMenu?.map((merchantItem, index) => {
                                return (
                                    <MenuItem
                                        sx={{ pl: 2, pr: 4 }}
                                        onClick={(event) => handleAvatarClick(merchantItem, merchantItem.isSelected, event)}
                                        key={`${merchantItem.merchantId}-${index}`}
                                    >
                                        <MerchantAvatar size="small" merchant={{ ...merchantItem }} />
                                        <Typography variant="h4" sx={{ ml: 2, fontWeight: 'normal' }}>
                                            {merchantItem.name}
                                        </Typography>
                                    </MenuItem>
                                );
                            })}
                        </Stack>
                    </PerfectScrollbar>
                </Menu>
            </>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchorMenu, avatarsMenu, open]);

    const someInMenuIsSelected = useMemo(() => {
        return avatarsMenu?.some((merchantItem) => {
            return merchantItem.isSelected;
        });
    }, [avatarsMenu]);

    return (
        <>
            {loading && (
                <Stack direction="row" spacing={0.5}>
                    <Skeleton variant="circular" width={30} height={30} />
                    <Skeleton variant="circular" width={30} height={30} />
                    <Skeleton variant="circular" width={30} height={30} />
                </Stack>
            )}
            {!loading && (
                <Stack direction={direction} spacing={spacing}>
                    {merchants?.slice(0, avatarsLimit)?.map((merchantItem, index) => {
                        return (
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                sx={{ ml: overlap ? -1 : 0.5, zIndex: 10 - index }}
                                key={`merchant-avatar-${index}-${merchantItem.merchantId}`}
                                onClick={(event) => {
                                    handleAvatarClick(merchantItem, merchantItem.isSelected, event);
                                }}
                            >
                                <MerchantAvatar
                                    readOnly={readOnly}
                                    size={size}
                                    disabled={disabled && !merchantItem.isSelected}
                                    merchant={{
                                        ...merchantItem
                                    }}
                                />
                                {showName && (
                                    <Typography variant="subtitle1" sx={{ cursor: 'pointer' }}>
                                        {merchantItem.name}
                                    </Typography>
                                )}
                            </Stack>
                        );
                    })}

                    {Boolean(avatarsMenu && avatarsMenu?.length) && (
                        <Box sx={{ ml: 1 }}>
                            <ShowMoreButton
                                isSelected={someInMenuIsSelected}
                                handleClick={(event: MouseEvent<HTMLElement>) => {
                                    setAnchorMenu(event.currentTarget);
                                }}
                                size={size}
                                moreText={`${avatarsMenu?.length ?? 0}`}
                            />
                        </Box>
                    )}

                    {optionsMenu}
                </Stack>
            )}
        </>
    );
};

/**
 * Single Merchant Selection:
 *
 * This component is used for views that require selecting just one merchant at a time.
 * It is useful for scenarios where only one merchant's data is needed to render the view,
 * such as on the Brands, Facets or Vendors.
 *
 * If @param saveLastSelection:boolean is TRUE the value state.auth.defaultSelectedMerchant will update
 *
 *  */
export const SingleMerchantSelector = ({
    value,
    onChange,
    size,
    blockFather,
    minimal,
    overlap,
    merchantsToShow,
    onAvatarClick,
    saveLastSelection = true
}: Omit<MerchantSelectorProps, 'multiple' | 'value' | 'onChange'> & {
    value: MerchantType | null;
    onChange: (val: MerchantType | null) => void;
    saveLastSelection?: boolean;
}) => {
    const dispatch = useDispatch();
    const { fatherMerchant, defaultSelectedMerchant } = useSelector((state) => state.auth);

    useEffect(() => {
        if (defaultSelectedMerchant && onChange) {
            onChange(defaultSelectedMerchant);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultSelectedMerchant, fatherMerchant]);

    return (
        <MerchantsSelector
            minimal={minimal}
            blockFather={blockFather}
            size={size}
            multiple={false}
            value={value ? [value] : null}
            overlap={overlap}
            onChange={(newValue) => {
                let newMerchant = newValue ? newValue[0] : null;

                if (newMerchant && saveLastSelection) {
                    dispatch(updateGlobalMerchant(newMerchant));
                }

                onChange(newMerchant);
            }}
            merchantsToShow={merchantsToShow}
            onAvatarClick={onAvatarClick}
        />
    );
};

const getAvatarCounter = (currentBreakPoint: Breakpoint, drawerOpen: boolean, minimal: boolean): number => {
    const counters: {
        [key in Breakpoint]: number | ((drawerOpen: boolean, minimal: boolean) => number);
    } = {
        xs: 3,
        sm: 6,
        md: (drawerOpen, minimal) => {
            if (drawerOpen) {
                return 7;
            }
            return minimal ? 7 : 10;
        },
        lg: (drawerOpen) => {
            return drawerOpen ? 5 : 6;
        },
        xl: (drawerOpen, minimal) => {
            if (drawerOpen) {
                return 6;
            }
            return minimal ? 8 : 10;
        }
    };

    const counterValue = counters[currentBreakPoint];

    if (typeof counterValue === 'function') {
        return counterValue(drawerOpen, minimal);
    }

    return counterValue;
};
